// Rates as people type them, to and from the contract's Rate (parts per 10^8). Unit conversions on digits only: a
// whole percent "50" is 50 000 000, "3.75 %" (hundredths of a percent, legacy `cash_discount_fee` 375) is 3 750 000.
import { rate, type Rate } from '@radish/primitives';

const PER_PERCENT = 1_000_000n;
const PER_HUNDREDTH = 10_000n;

export const rateFromWholePercent = (percent: bigint): Rate => rate(percent * PER_PERCENT);

export const rateFromHundredths = (hundredths: bigint): Rate => rate(hundredths * PER_HUNDREDTH);

/** A rate as a percent string, trailing zeros dropped: 50 000 000 → "50", 3 750 000 → "3.75". */
export function percentText(r: Rate): string {
  const digits = r.scaled.toString().padStart(7, '0');
  const whole = digits.slice(0, -6);
  const fraction = digits.slice(-6).replace(/0+$/, '');
  return fraction === '' ? whole : `${whole}.${fraction}`;
}

/** A rate in hundredths of a percent, truncated (the Cash Discount Rate field's 2 decimals). */
export function hundredthsOf(r: Rate): bigint {
  const digits = r.scaled.toString().padStart(5, '0');
  return BigInt(digits.slice(0, -4));
}

/** A rate as whole percent when it is one (the % discount field), else null. */
export function wholePercentOf(r: Rate): bigint | null {
  const digits = r.scaled.toString().padStart(7, '0');
  return /^0+$/.test(digits.slice(-6)) ? BigInt(digits.slice(0, -6)) : null;
}

const PER_TEN_THOUSANDTH = 100n;

/** A rate typed in ten-thousandths of a percent (the Tax Rate field's 4 decimals, legacy `#.####`): 8.2500 % is 82 500. */
export const rateFromTenThousandths = (tenThousandths: bigint): Rate => rate(tenThousandths * PER_TEN_THOUSANDTH);

/** A rate in ten-thousandths of a percent, truncated. */
export function tenThousandthsOf(r: Rate): bigint {
  const digits = r.scaled.toString().padStart(3, '0');
  return BigInt(digits.slice(0, -2));
}
